/**
 * Copies text to the clipboard with enhanced error handling and compatibility
 * @param {string} text - The text to copy to clipboard
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const copyToClipboard = async (text) => {
  if (!text || typeof text !== 'string') {
    console.error('Invalid text provided for clipboard copy');
    return false;
  }

  try {
    // Check if Clipboard API is available and we're in a secure context
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers or non-secure contexts
      return await fallbackCopyToClipboard(text);
    }
  } catch (error) {
    console.error("Error with Clipboard API:", error);
    // Try fallback method
    return await fallbackCopyToClipboard(text);
  }
};

/**
 * Fallback method using document.execCommand
 * @param {string} text - The text to copy
 * @returns {Promise<boolean>} - Success status
 */
const fallbackCopyToClipboard = async (text) => {
  return new Promise((resolve) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement("textarea");
      textArea.value = text;
      
      // Make it invisible but keep it in the DOM
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
      textArea.style.zIndex = "-1";
      
      // Add to DOM
      document.body.appendChild(textArea);
      
      // Focus and select the text
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);
      
      // Try to copy
      const successful = document.execCommand("copy");
      
      // Clean up
      document.body.removeChild(textArea);
      
      resolve(successful);
    } catch (error) {
      console.error("Fallback copy method failed:", error);
      resolve(false);
    }
  });
};

/**
 * Enhanced copy function with user interaction requirement check
 * @param {string} text - The text to copy
 * @param {Event} event - The user interaction event (click, etc.)
 * @returns {Promise<boolean>} - Success status
 */
export const copyToClipboardWithUserInteraction = async (text, event) => {
  // Ensure we have a user interaction event
  if (!event || !event.isTrusted) {
    console.warn('Copy operation requires a user interaction event');
    return false;
  }

  return await copyToClipboard(text);
};

/**
 * Copies text to clipboard and shows a success/error message
 * @param {string} text - The text to copy to clipboard
 * @param {Function} onSuccess - Callback function to execute on success
 * @param {Function} onError - Callback function to execute on error
 * @param {Event} event - Optional user interaction event
 * @returns {Promise<void>}
 */
export const copyToClipboardWithFeedback = async (text, onSuccess, onError, event = null) => {
  let success = false;
  
  if (event) {
    success = await copyToClipboardWithUserInteraction(text, event);
  } else {
    success = await copyToClipboard(text);
  }
  
  if (success) {
    if (onSuccess) onSuccess();
  } else {
    if (onError) onError();
  }
};

/**
 * Check if clipboard functionality is available
 * @returns {boolean} - True if clipboard is available
 */
export const isClipboardAvailable = () => {
  return !!(navigator.clipboard || document.queryCommandSupported?.('copy'));
};

/**
 * Get clipboard availability status with details
 * @returns {Object} - Status object with details
 */
export const getClipboardStatus = () => {
  const hasClipboardAPI = !!(navigator.clipboard);
  const isSecureContext = window.isSecureContext;
  const hasExecCommand = !!document.queryCommandSupported?.('copy');
  
  return {
    available: hasClipboardAPI || hasExecCommand,
    clipboardAPI: hasClipboardAPI,
    secureContext: isSecureContext,
    execCommand: hasExecCommand,
    recommended: hasClipboardAPI && isSecureContext
  };
}; 